import { useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Camera, FileDown, Plus, Upload } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Badge, Button, Card, CardBody, Tabs, TabsContent, TabsList, TabsTrigger } from '@vnetwork/ui/primitives';
import { Stat, StatGroup } from '@vnetwork/ui/data';
import { useToast } from '@vnetwork/ui/feedback';
import { useCustomerCounts, type CustomerCounts } from '@/api/customers';
import { useMe } from '@/api/me';
import { CustomerListSection } from './components/CustomerListSection';
import { CUSTOMER_SEGMENTS } from './list-configs/customer-segment-columns';

/**
 * Customers — the ported DataCentral v4 customer manager. A `PageHeader` with the
 * legacy action set (Scan cards / Import template / Import customer / Add) over a
 * `Tabs` segment switcher (Qualified Prospects · Cool Leads · Active · Trial ·
 * Stop · Dead Lead · Deleted). Each tab label carries its live count from
 * `useCustomerCounts`; the active tab is persisted in the URL `?tab=`. The
 * Deleted segment is admin-only. Excel import / scan-cards wiring is Phase 2 —
 * the buttons are present but surface a "coming soon" toast.
 */
export default function CustomersPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: me } = useMe();
  const isAdmin = me?.is_admin === true;

  const { data: countsData } = useCustomerCounts();
  const counts = countsData?.result;

  const segments = useMemo(
    () => CUSTOMER_SEGMENTS.filter((s) => !s.adminOnly || isAdmin),
    [isAdmin],
  );

  const urlTab = searchParams.get('tab');
  const activeTab = segments.some((s) => s.segment === urlTab)
    ? (urlTab as string)
    : segments[0]?.segment ?? 'qualified-prospects';

  const setTab = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  const notImplemented = (label: string) =>
    toast.show({ tone: 'info', title: label, description: 'Coming soon.' });

  return (
    <Container size="full">
      <PageHeader
        title="Customers"
        description="Manage prospects, leads and active customers across their lifecycle."
        style={{ marginBottom: 'var(--vn-space-4)' }}
        actions={
          <>
            <Button variant="secondary" startIcon={<Camera size={15} />} onClick={() => notImplemented('Scan cards')}>
              Scan cards
            </Button>
            <Button variant="secondary" startIcon={<FileDown size={15} />} onClick={() => notImplemented('Import template')}>
              Import template
            </Button>
            <Button variant="secondary" startIcon={<Upload size={15} />} onClick={() => notImplemented('Import customer')}>
              Import customer
            </Button>
            <Button startIcon={<Plus size={15} />} onClick={() => navigate('/customers/new')}>
              Add customer
            </Button>
          </>
        }
      />

      <Card flat style={{ marginBottom: 'var(--vn-space-4)' }}>
        <CardBody>
          <StatGroup columns={4}>
            <Stat
              label="Qualified Prospects"
              dotColor="var(--vn-info-solid)"
              value={formatCount(counts, 'qualified_prospects')}
            />
            <Stat label="Active" dotColor="var(--vn-success-solid)" value={formatCount(counts, 'active')} />
            <Stat label="Trial" dotColor="var(--vn-warning-solid)" value={formatCount(counts, 'trial')} />
            <Stat label="Dead Lead" dotColor="var(--vn-danger-solid)" value={formatCount(counts, 'dead_leads')} />
          </StatGroup>
        </CardBody>
      </Card>

      <Tabs value={activeTab} onValueChange={setTab}>
        <TabsList style={{ marginBottom: 0, flexWrap: 'wrap' }}>
          {segments.map((s) => (
            <TabsTrigger key={s.segment} value={s.segment}>
              {s.label}
              <Badge
                tone={activeTab === s.segment ? 'primary' : 'neutral'}
                style={{ marginLeft: 'var(--vn-space-2)', fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCount(counts, s.countKey)}
              </Badge>
            </TabsTrigger>
          ))}
        </TabsList>

        {segments.map((s) => (
          <TabsContent key={s.segment} value={s.segment}>
            {activeTab === s.segment ? (
              <CustomerListSection
                segment={s.segment}
                columns={s.columns}
                filterFields={s.filterFields}
                searchPlaceholder={s.searchPlaceholder}
              />
            ) : null}
          </TabsContent>
        ))}
      </Tabs>
    </Container>
  );
}

/** Tab badge count → grouped string, e.g. 1234 → "1,234"; missing → "0". */
function formatCount(counts: CustomerCounts | undefined, key: keyof CustomerCounts): string {
  const n = counts?.[key];
  return typeof n === 'number' ? n.toLocaleString('en-US') : '0';
}
